import { customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_FIREWALL_NETWORK_TABLE } from '../../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsTableWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/dataTable';
import { html, TemplateResult } from 'lit';
import { GRID_ITEM_FORM_ID } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/base/component/GzPolicyUiSettingsBaseGrid';
import { FirewallNetworkIdentification, FirewallNetworkItem } from '@nirvana/shared/dtos/types/policies/common';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import { sanitizeNetworkItemFormValue } from '../../utils/converters';
import {
  ipValidatorByIdentification,
  validateIdentificationDuplicates,
  validateNetworkDuplicates,
} from '../../utils/validators';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import { RequiredValidator } from '../../../../../../../../ui/gzForm/validators/required.validator';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { CustomizableValidator } from '../../../../../../../../ui/gzForm/validators/customizable.validator';
import { GzFirewallNetworkItemFormWebComponent } from './GzFirewallNetworkItemFormWebComponent';
import { ColumnKeys, getTableColumns } from './grid-config';

@customElement(WEB_COMPONENT_POLICY_PANEL_FIREWALL_NETWORK_TABLE)
export class GzFirewallNetworkTableWebComponent extends GzPolicyUiSettingsTableWebComponent<
  FirewallNetworkItem,
  FirewallNetworkItem
> {
  protected hasReorderRowAction = false;
  private editedIdentification: FirewallNetworkIdentification;

  protected columns = getTableColumns([
    RequiredValidator(translateFn('Required field')),
    CustomizableValidator((value: string) => ipValidatorByIdentification(value, this.editedIdentification)),
  ]);

  protected renderGridItemForm(): TemplateResult {
    return html`
      <gz-policy-panel-firewall-network-item id="${GRID_ITEM_FORM_ID}"></gz-policy-panel-firewall-network-item>
    `;
  }

  protected sanitizeGridItemBeforeAdd(gridItemFormValue: FirewallNetworkItem): FirewallNetworkItem {
    return sanitizeNetworkItemFormValue(gridItemFormValue);
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: FirewallNetworkItem): ValidationResult {
    // check for duplicates
    const networkDuplicatesValidation = validateNetworkDuplicates(gridItemFormValue, this.gridComponent.getFormValue());
    if (!networkDuplicatesValidation.valid) {
      (this.gridItemForm as GzFirewallNetworkItemFormWebComponent).setItemNetworkError(
        networkDuplicatesValidation.error
      );
      return networkDuplicatesValidation;
    }

    // check for duplicates
    const identificationDuplicatesValidation = validateIdentificationDuplicates(
      gridItemFormValue,
      this.gridComponent.getFormValue()
    );
    if (!identificationDuplicatesValidation.valid) {
      (this.gridItemForm as GzFirewallNetworkItemFormWebComponent).setItemIdentificationError(
        identificationDuplicatesValidation.error
      );
    }

    return identificationDuplicatesValidation;
  }

  protected onEditRowEnabled(event: CustomEvent) {
    super.onEditRowEnabled(event);
    this.editedIdentification = event.detail.item.identification;

    const editedFields = this.gridComponent.shadowRoot?.querySelectorAll('.edit-form-field');
    if (editedFields) {
      editedFields.forEach((field: GzFormFieldWebComponent) => {
        if (field.id.split('-')[0] === ColumnKeys.MAC) {
          field.disabled = this.editedIdentification === FirewallNetworkIdentification.NETWORK;
        }
      });
    }
  }

  protected validateGridItemBeforeEdit(oldItem: FirewallNetworkItem, newItem: FirewallNetworkItem): ValidationResult {
    // skip if item is not changed
    const hasSameNetwork = oldItem.network === newItem.network;
    const hasSameIdentification =
      newItem.identification === oldItem.identification &&
      newItem.domainMac === oldItem.domainMac &&
      newItem.ip === oldItem.ip;
    if (hasSameNetwork && hasSameIdentification) {
      return { valid: true };
    }

    // check for duplicates if network is changed
    const networkDuplicatesValidation: ValidationResult = hasSameNetwork
      ? { valid: true }
      : validateNetworkDuplicates(newItem, this.gridComponent.getFormValue());
    if (!networkDuplicatesValidation.valid) {
      return networkDuplicatesValidation;
    }

    // check for duplicates if identification is changed
    return hasSameIdentification
      ? { valid: true }
      : validateIdentificationDuplicates(newItem, this.gridComponent.getFormValue());
  }
}
